import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  getRothBeneficiaryOption,
  getUserAddedRothBeneficiariesAsLists,
} from '../../../../redux/modules/rothConversionTransaction/rothConversionTransaction.selectors';
import { getMappedBeneficiaries } from '../../../../redux/modules/beneficiaries/beneficiaries.selectors';
import If from '../../../Shared/If/If';
import RadioButton from '../../../Shared/RadioButton/RadioButton';
import { ROTH_CONVERSION_BENEFICIARY_OPTIONS } from '../../../../settings/settings';
import { moduleActions } from '../../../../redux/modules';
import BeneficiaryList from '../BeneficiaryList/BeneficiaryList';
import './BeneficiaryForm.scss';
import RothConversionBeneNote from '../RothConversionBeneNote';

/**
 * Roth Conversion Beneficiary Form
 */
const RothBeneficiaryFrom = () => {
  const dispatch = useDispatch();
  const { updateRothBeneficiaryOption } = moduleActions;
  // const [newBeneficiaries, setNewBeneficiaries] = useState();

  /**
   * Maybe a case where if the user has existing Roth IRA beneficiaries
   * then that will be the only to show
   */

  // selectors
  const selectedBeneficiaryOption = useSelector(getRothBeneficiaryOption);
  const previousSelectedBeneficiaryOption = useRef();
  // new beneficiaries
  const userAddedBeneficiaries = useSelector(
    getUserAddedRothBeneficiariesAsLists
  );

  /**
   * callback for selecting beneficiary type
   *
   * TODO: will need to figure out what to set when user selects
   * using existing beneficiaries
   */
  const onOptionSelect = useCallback(
    (value, beneficiaries, i) =>
      dispatch(updateRothBeneficiaryOption(value, beneficiaries, i)),
    [dispatch, updateRothBeneficiaryOption]
  );

  // Get existing ira beneficiaries
  const existingBeneficiaries = useSelector(getMappedBeneficiaries);
  const selectedBeneIndex = useSelector(
    (state) => state.rothConversionTransaction.beneficiaryIndex
  );

  useEffect(() => {
    if (
      existingBeneficiaries.RothIRA?.length === 1 &&
      selectedBeneficiaryOption === null
    ) {
      previousSelectedBeneficiaryOption.current = selectedBeneficiaryOption;
      existingBeneficiaries.RothIRA?.map((beneficiaryGroup, i) => {
        return dispatch(
          updateRothBeneficiaryOption(
            ROTH_CONVERSION_BENEFICIARY_OPTIONS.EXISTING_ROTH_IRA,
            beneficiaryGroup,
            i
          ),
          [
            dispatch,
            updateRothBeneficiaryOption,
            existingBeneficiaries.RothIRA[i].beneficiaryGroups,
            ROTH_CONVERSION_BENEFICIARY_OPTIONS.EXISTING_ROTH_IRA,
            selectedBeneficiaryOption,
          ]
        );
      });
    }
  });

  const existingNonRothBeneficiariesMap = [
    {
      value: ROTH_CONVERSION_BENEFICIARY_OPTIONS.EXISTING_TRADITIONAL_IRA,
      label: 'Use existing Traditional IRA beneficiaries',
      id: 'traditional_ira',
      beneficiaryGroups: existingBeneficiaries.TraditionalIRA || [],
    },
    {
      value: ROTH_CONVERSION_BENEFICIARY_OPTIONS.EXISTING_ROLLOVER_IRA,
      label: 'Use existing Rollover IRA beneficiaries',
      id: 'rollover_ira',
      beneficiaryGroups: existingBeneficiaries.RolloverIRA || [],
    },
  ];

  /**
   * @param {Beneficiary[]}
   * @return { {primary: <Beneficiary[]>, secondary: <Beneficiaryp[]>} }
   */
  const mapBeneficiaries = useCallback(
    (group) =>
      group.reduce(
        (acc, b) => {
          acc[b.type].push(b);
          return acc;
        },
        { primary: [], secondary: [] }
      ),
    []
  );

  // If user has existing Roth Beneficiaries, hide everything else
  return (
    <div>
      <section className="roth-conversion-beneficiaryform">
        <legend>
          Changes made to beneficiaries will be applied to all Roth IRA
          investments.
        </legend>

        <If test={existingBeneficiaries.RothIRA?.length > 0}>
          {existingBeneficiaries.RothIRA?.map((beneficiaryGroup) => {
            return (
              <div
                className="roth-conversion-beneficiaryform_row"
                key={uuidv4()}
              >
                <strong>Use existing Roth IRA beneficiaries</strong>
                <BeneficiaryList
                  beneficiaries={mapBeneficiaries(beneficiaryGroup)}
                  displayOnly={true}
                />
              </div>
            );
          })}
        </If>

        {/* logic to check for existing NON roth funds will need to be made here */}
        <If test={!existingBeneficiaries.RothIRA?.length}>
          {existingNonRothBeneficiariesMap.map((option) => (
            <If test={option.beneficiaryGroups} key={uuidv4()}>
              {option.beneficiaryGroups.map((beneficiaryGroup, i) => {
                return (
                  <div className="roth-conversion-beneficiaryform_row">
                    <RadioButton
                      category={'beneficiarySelectionOption'}
                      value={option.value}
                      id={option.id}
                      onChange={() =>
                        onOptionSelect(option.value, beneficiaryGroup, i)
                      }
                      className="roth-conversion-beneficiaryform_option"
                      checked={
                        selectedBeneficiaryOption === option.value &&
                        selectedBeneIndex === i
                      }
                      labelText={<strong>{option.label}</strong>}
                      required={true}
                    />
                    <BeneficiaryList
                      beneficiaries={mapBeneficiaries(beneficiaryGroup)}
                      displayOnly={true}
                    />
                  </div>
                );
              })}
            </If>
          ))}

          <div className="roth-conversion-beneficiaryform_row">
            <RadioButton
              category={'beneficiarySelectionOption'}
              value={ROTH_CONVERSION_BENEFICIARY_OPTIONS.CREATE_NEW}
              id="new"
              onChange={onOptionSelect}
              className="roth-conversion-beneficiaryform_option"
              checked={
                selectedBeneficiaryOption ===
                ROTH_CONVERSION_BENEFICIARY_OPTIONS.CREATE_NEW
              }
              labelText={<strong>Create new beneficiaries</strong>}
              required={true}
            />
            <BeneficiaryList
              beneficiaries={userAddedBeneficiaries}
              disabled={
                selectedBeneficiaryOption !==
                ROTH_CONVERSION_BENEFICIARY_OPTIONS.CREATE_NEW
              }
            />
          </div>

          <div className="roth-conversion-beneficiaryform_row">
            <RadioButton
              category={'beneficiarySelectionOption'}
              value={ROTH_CONVERSION_BENEFICIARY_OPTIONS.SETUP_LATER}
              id="later"
              onChange={onOptionSelect}
              className="roth-conversion-beneficiaryform_option"
              checked={
                selectedBeneficiaryOption ===
                ROTH_CONVERSION_BENEFICIARY_OPTIONS.SETUP_LATER
              }
              labelText={<strong>Setup later</strong>}
              required={true}
            />
          </div>
        </If>
      </section>
      {!existingBeneficiaries.RothIRA?.length && (
        <div className="required-field">
          <span>*</span> required field note
        </div>
      )}
      <RothConversionBeneNote />
    </div>
  );
};

export default RothBeneficiaryFrom;
